import type { Orientation, OrientationLimits } from './orientation';

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────
// LA GUIDA PER I DUE SCENARI (ADR-022, ADR-031, WP1): cosa deve vedere la fotocamera e da dove si scatta.
//  · Postazione: si sta a un ANGOLO del pallet e si vedono DUE facce; profondità e altezza si misurano.
//  · Scaffale: si sta DAVANTI e si vede la sola faccia frontale; la profondità viene dal tipo ed è dichiarata assunta.
// Lo stesso testo e lo stesso disegno servono al recorder (campagna) e alla schermata operatore: ciò che si impara in magazzino
// è ciò che l'operatore farà. Le coordinate dei disegni sono nel riquadro del video 3:4 (300×400) come `GuideScreen`.
// Perché serve l'avviso sull'altezza di scatto: nella prova a secco del 20/10/2026 le foto da telefono scattate molto dall'alto
// (~40° di inclinazione) erano quelle con i residui di posa peggiori; la livella dice quando si è fuori dai limiti.
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────

export type ScenarioKey = 'Postazione' | 'Scaffale';

export const GUIDE_VIEWBOX = { widthPx: 300, heightPx: 400 } as const;

export interface GuideShape {
  points: ReadonlyArray<readonly [number, number]>;
  closed: boolean;
  /** Tratteggiata = «qui deve stare»; piena = il bordo che conta di più (la base). */
  dashed: boolean;
}

export interface ScenarioGuide {
  key: ScenarioKey;
  title: string;
  /** Una riga, per lo spazio stretto di una pistola 320×405. */
  short: string;
  steps: readonly string[];
  shapes: readonly GuideShape[];
}

const GUIDES: Record<ScenarioKey, ScenarioGuide> = {
  Postazione: {
    key: 'Postazione',
    title: 'A terra, all’angolo',
    short: 'All’angolo, a 2,5–3,5 m: due facce, base intera, etichetta.',
    steps: [
      'Mettiti a un angolo del pallet, a 2,5–3,5 m.',
      'Devono vedersi due facce, la base intera e l’etichetta.',
      'Pistola all’altezza del petto, dritta: non dall’alto.',
    ],
    // La base vista da un angolo: il vertice vicino in basso, quello lontano in alto.
    shapes: [{ points: [[150, 262], [250, 230], [150, 196], [50, 230]], closed: true, dashed: true }],
  },
  Scaffale: {
    key: 'Scaffale',
    title: 'A scaffale, di fronte',
    short: 'Davanti, a 2,5–3,5 m: faccia frontale intera, piedini, etichetta.',
    steps: [
      'Mettiti davanti al pallet, centrato, a 2,5–3,5 m.',
      'Deve vedersi tutta la faccia frontale, i piedini e l’etichetta.',
      'Pistola dritta all’altezza del petto: la profondità è quella del tipo.',
    ],
    // La faccia frontale come un rettangolo, con la base in evidenza.
    shapes: [
      { points: [[40, 112], [260, 112], [260, 262], [40, 262]], closed: true, dashed: true },
      { points: [[40, 262], [260, 262]], closed: false, dashed: false },
    ],
  },
};

export function guideFor(scenario: ScenarioKey): ScenarioGuide {
  return GUIDES[scenario];
}

// ── La livella: dove sta l'inclinazione rispetto ai limiti ───────────────────────────────────────────────────

/** L'asse della livella, in gradi di inclinazione verso il basso: da −30 (in su) a +60 (molto dall'alto). */
export const PITCH_AXIS = { minDeg: -30, maxDeg: 60 } as const;

/** Il tratto verticale della livella nel riquadro 300×400. */
export const PITCH_BAR = { x: 16, topPx: 110, bottomPx: 290 } as const;

export interface PitchGaugeGeometry {
  barTopPx: number;
  barBottomPx: number;
  /** La zona buona, fra i due limiti. */
  zoneTopPx: number;
  zoneBottomPx: number;
  /** Il segno dell'inclinazione di adesso; null senza sensore. */
  markerPx: number | null;
  inZone: boolean | null;
}

function yOf(pitchDownDeg: number): number {
  const clamped = Math.max(PITCH_AXIS.minDeg, Math.min(PITCH_AXIS.maxDeg, pitchDownDeg));
  const t = (clamped - PITCH_AXIS.minDeg) / (PITCH_AXIS.maxDeg - PITCH_AXIS.minDeg);
  return PITCH_BAR.topPx + t * (PITCH_BAR.bottomPx - PITCH_BAR.topPx);
}

export function pitchGauge(orientation: Orientation | null, limits: OrientationLimits): PitchGaugeGeometry {
  return {
    barTopPx: PITCH_BAR.topPx,
    barBottomPx: PITCH_BAR.bottomPx,
    zoneTopPx: yOf(-limits.maxPitchUpDeg),
    zoneBottomPx: yOf(limits.maxPitchDownDeg),
    markerPx: orientation ? yOf(orientation.pitchDownDeg) : null,
    inZone: orientation ? orientation.pitchDownDeg >= -limits.maxPitchUpDeg && orientation.pitchDownDeg <= limits.maxPitchDownDeg : null,
  };
}
